import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import EmailPreview from './EmailPreview';
const TemplateGallery = () => {
  const [activeTemplate, setActiveTemplate] = useState<number | null>(null);
  const isMobile = useIsMobile();
  const {
    toast
  } = useToast();
  const [downloading, setDownloading] = useState<{
    [key: number]: boolean;
  }>({});
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
        }
      });
    }, {
      threshold: 0.1
    });
    document.querySelectorAll('.template-item').forEach(el => {
      observer.observe(el);
    });
    return () => {
      document.querySelectorAll('.template-item').forEach(el => {
        observer.unobserve(el);
      });
    };
  }, []);

  // Updated templates with real HTML preview links
  const templates = [{
    id: 1,
    name: "Fresh Start with NIVEA - Your April Skincare Essentials",
    description: "Spring into the new season with skincare tips, product highlights, and exclusive NIVEA offers to keep your glow going all month long.",
    desktop: "https://fdvcnbruddgrdnmbpsqc.supabase.co/storage/v1/object/public/email_templates//Fresh-Start-with-NIVEA-Your-April-Skincare-Essentials.html",
    mobile: "https://fdvcnbruddgrdnmbpsqc.supabase.co/storage/v1/object/public/email_templates//Fresh-Start-with-NIVEA-Your-April-Skincare-Essentials.html",
    primaryColor: "bg-blue-500",
    secondaryColor: "bg-blue-100"
  }, {
    id: 2,
    name: "Discover the Labareau Science of Timeless Beauty",
    description: "Uncover the philosophy behind Labareau's high-performance skincare and how our formulas work to visibly rejuvenate and restore your skin from within.",
    desktop: "https://fdvcnbruddgrdnmbpsqc.supabase.co/storage/v1/object/public/email_templates//Discover-the-Labareau-Science-of-Timeless-Beauty.html",
    mobile: "https://fdvcnbruddgrdnmbpsqc.supabase.co/storage/v1/object/public/email_templates//Discover-the-Labareau-Science-of-Timeless-Beauty.html",
    primaryColor: "bg-purple-500",
    secondaryColor: "bg-purple-100"
  }, {
    id: 3,
    name: "Wildride Early Black Friday Access - 15% Off + Free Gift!",
    description: "Get a head start on holiday deals! Enjoy 15% off Wildride toddler carriers and receive a stylish scarf FREE with every purchase. Limited time only while supplies last!",
    desktop: "https://fdvcnbruddgrdnmbpsqc.supabase.co/storage/v1/object/public/email_templates//Wildride-Early-Black-Friday-Access-15-Off-Free-Gift.html",
    mobile: "https://fdvcnbruddgrdnmbpsqc.supabase.co/storage/v1/object/public/email_templates//Wildride-Early-Black-Friday-Access-15-Off-Free-Gift.html",
    primaryColor: "bg-orange-500",
    secondaryColor: "bg-orange-100"
  }, {
    id: 4,
    name: "Spring Into Adventure with the All-Electric Dacia Spring",
    description: "Get ready for the season of exploration with the bold, electric Dacia Spring. Discover exciting offers, explore the new Bigster, and experience Dacia's spirit of adventure on the road and beyond.",
    desktop: "https://fdvcnbruddgrdnmbpsqc.supabase.co/storage/v1/object/public/email_templates//Spring-Into-Adventure-with-the-All-Electric-Dacia-Spring.html",
    mobile: "https://fdvcnbruddgrdnmbpsqc.supabase.co/storage/v1/object/public/email_templates//Spring-Into-Adventure-with-the-All-Electric-Dacia-Spring.html",
    primaryColor: "bg-green-500",
    secondaryColor: "bg-green-100"
  }, {
    id: 5,
    name: "Renault Symbioz - Orders Are Now Open",
    description: "Experience the new Renault Symbioz where innovation meets comfort. With up to 1,000 km of range, advanced hybrid tech, and a smart panoramic sunroof, it's the family SUV designed to elevate every drive.",
    desktop: "https://fdvcnbruddgrdnmbpsqc.supabase.co/storage/v1/object/public/email_templates//Renault-Symbioz-Orders-Are-Now-Open.html",
    mobile: "https://fdvcnbruddgrdnmbpsqc.supabase.co/storage/v1/object/public/email_templates//Renault-Symbioz-Orders-Are-Now-Open.html",
    primaryColor: "bg-pink-500",
    secondaryColor: "bg-pink-100"
  }, {
    id: 6,
    name: "Your Early Access Starts Tomorrow - smart #1 Launch Edition",
    description: "Be among the first to secure the exclusive smart #1 Launch Edition. Only 1,000 available. Pre-order your voucher starting this YEAR and enjoy priority access, exclusive design features, and peace of mind with a refundable deposit.",
    desktop: "https://fdvcnbruddgrdnmbpsqc.supabase.co/storage/v1/object/public/email_templates//Your-Early-Access-Starts-Tomorrow-smart%20-1-Launch-Edition.html",
    mobile: "https://fdvcnbruddgrdnmbpsqc.supabase.co/storage/v1/object/public/email_templates//Your-Early-Access-Starts-Tomorrow-smart%20-1-Launch-Edition.html",
    primaryColor: "bg-teal-500",
    secondaryColor: "bg-teal-100"
  }];
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setTimeout(() => {
        setActiveTemplate(null);
      }, 300);
    }
  };
  const downloadTemplate = async (url: string, fileName: string) => {
    const templateId = templates.find(t => t.desktop === url)?.id || 0;
    if (templateId) {
      setDownloading(prev => ({
        ...prev,
        [templateId]: true
      }));
    }
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName.replace(/\s+/g, '-') + '.html';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast({
        title: "Download Successful",
        description: `${fileName} has been downloaded.`,
        duration: 3000
      });
    } catch (error) {
      console.error("Error downloading template:", error);
      toast({
        title: "Download Failed",
        description: "There was an error downloading the template. Please try again later.",
        variant: "destructive",
        duration: 3000
      });
    } finally {
      if (templateId) {
        setDownloading(prev => ({
          ...prev,
          [templateId]: false
        }));
      }
    }
  };
  return <section id="templates" className="px-4 bg-gradient-to-b from-gray-50 to-white py-[40px]">
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">My <span className="text-primary">Portfolio</span></h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">Browse our collection of professionally designed email templates.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
          {templates.map((template, index) => <Dialog key={template.id} onOpenChange={handleOpenChange}>
              <DialogTrigger asChild>
                <div className={cn("template-item cursor-pointer rounded-xl overflow-hidden shadow-md opacity-0 translate-y-8 transition-all duration-700", `delay-[${index * 100}ms]`)} onClick={() => setActiveTemplate(template.id)} role="button" tabIndex={0}>
                  <div className={cn("h-48 relative", template.primaryColor)}>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-3/4 h-32 bg-white rounded-lg shadow-lg flex items-center justify-center">
                        <div className={cn("w-10 h-10 rounded-full", template.primaryColor)}></div>
                      </div>
                    </div>
                    <div className="overlay absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                      <span className="text-white font-medium text-lg">View Template</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white">
                    <h3 className="font-semibold text-lg text-secondary mb-1">{template.name}</h3>
                    <p className="text-gray-600 text-sm">{template.description}</p>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className={cn("w-full max-w-[80vw] sm:max-w-[700px] lg:max-w-[900px] xl:max-w-[1100px] 2xl:max-w-[1300px] p-3 sm:p-6", isMobile ? "h-[90vh] overflow-y-auto" : "")}>
                <div className="flex flex-col pt-4">
                  <DialogTitle className="text-base sm:text-lg lg:text-xl font-bold mb-2 pr-8">{template.name}</DialogTitle>
                  <DialogDescription className="text-sm sm:text-base mb-4 sm:mb-6">
                    {template.description}
                  </DialogDescription>
                  
                  <div className="bg-muted p-2 sm:p-4 rounded-lg mb-4 sm:mb-6">
                    <EmailPreview templateUrl={template.desktop} templateName={template.name} />
                    <p className="mt-2 sm:mt-4 text-xs sm:text-sm text-muted-foreground italic">
                      This email template is designed and developed by Antonio Dimoski and is provided for demonstration purposes only. 
                      It showcases the quality and style of work that can be achieved.
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>)}
        </div>
      </div>
    </section>;
};
export default TemplateGallery;